import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TeamInfo } from '../TeamInfo';
import { UseLiveStrategy, LiveTeamPlan } from '../hooks/useLiveStrategy';
import { describeStop } from '../analytics/liveStrategy';
import { clockAt, formatMinutes } from '../analytics/stintPlan';
import { navigate } from './EventsList';
import StintGrid from './StintGrid';
import './LiveStrategyPanel.css';

interface LiveStrategyPanelProps {
  live: UseLiveStrategy;
  teams: Map<string, TeamInfo>;
  /** The race being followed; null when nothing is being recorded. */
  eventId: string | null;
  /** How early a stop is called "soon". */
  dueSoonMinutes: number;
}

/** "8:12" for 8 minutes 12 seconds; the sign is carried separately. */
function formatCountdown(minutes: number): string {
  const total = Math.round(Math.abs(minutes) * 60);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function teamLabel(team: TeamInfo | undefined, fallback: string): string {
  if (!team) return fallback;
  const name = team.teamName || team.driver;
  return `#${team.kartNumber || '—'}${name ? ` ${name}` : ''}`;
}

type Urgency = 'calm' | 'soon' | 'now' | 'overdue';

function urgencyOf(inMinutes: number, dueSoon: number): Urgency {
  if (inMinutes < -2) return 'overdue';
  if (inMinutes <= 0) return 'now';
  if (inMinutes <= dueSoon) return 'soon';
  return 'calm';
}

/**
 * A strip that appears by itself when a planned stop is close, so the prompt does
 * not depend on anybody having the stint plan panel open. Silent the rest of the
 * time.
 */
export const StopPrompts: React.FC<{
  plans: LiveTeamPlan[];
  teams: Map<string, TeamInfo>;
  dueSoonMinutes: number;
}> = ({ plans, teams, dueSoonMinutes }) => {
  const due = plans
    .filter((p) => !p.live.boxed && p.live.nextStop && p.live.nextStop.inMinutes <= dueSoonMinutes)
    .sort((a, b) => a.live.nextStop!.inMinutes - b.live.nextStop!.inMinutes);

  if (due.length === 0) return null;

  return (
    <div className="lsp-prompts" role="status" aria-label="Planned stops">
      {due.map((p) => {
        const stop = p.live.nextStop!;
        const urgency = urgencyOf(stop.inMinutes, dueSoonMinutes);
        return (
          <div key={p.strategy.id} className={`lsp-prompt lsp-prompt--${urgency}`}>
            <b>{teamLabel(teams.get(p.teamId), p.strategy.title)}</b>{' '}
            {stop.inMinutes <= 0
              ? stop.inMinutes < -2
                ? `${formatCountdown(stop.inMinutes)} overdue`
                : 'box now'
              : `box in ${formatCountdown(stop.inMinutes)}`}
            {' — '}
            {describeStop(stop)}
          </div>
        );
      })}
    </div>
  );
};

const PlanCard: React.FC<{
  item: LiveTeamPlan;
  team: TeamInfo | undefined;
  live: UseLiveStrategy;
  dueSoonMinutes: number;
}> = ({ item, team, live, dueSoonMinutes }) => {
  const { strategy, live: plan } = item;
  const { nextStop, clock } = plan;
  const gridRef = useRef<HTMLDivElement>(null);

  const errors = plan.violations.filter((v) => v.severity === 'error');
  const midpoint = plan.summary.constraints.closedPitWindows.find((w) => w.kind === 'midpoint');
  const saving = live.savingId === strategy.id;

  // Keep the stint in the kart in view as the race moves on.
  useEffect(() => {
    const row = gridRef.current?.querySelector('[aria-current="step"]');
    (row as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [plan.currentIndex]);

  return (
    <section className="lsp-card" aria-label={`Stint plan for ${teamLabel(team, strategy.title)}`}>
      <header className="lsp-head">
        <span className="lsp-team">{teamLabel(team, strategy.kartNumber ? `#${strategy.kartNumber}` : 'Team')}</span>
        <span className="lsp-title">{strategy.title}</span>
        {clock.source === 'estimated' && (
          <span
            className="lsp-badge lsp-badge--warn"
            title="No session countdown yet, so the clock is the planned start time. Stops are not being recorded."
          >
            clock estimated
          </span>
        )}
        {item.dirty && <span className="lsp-badge">unsaved edits</span>}
        <button
          type="button"
          className="lsp-link"
          onClick={() => navigate(`/strategy?id=${strategy.id}`)}
        >
          Open in planner →
        </button>
      </header>

      {plan.boxed ? (
        <div className="lsp-next lsp-next--pit" role="status">
          <b>In the pit.</b> Stint {plan.currentIndex + 1} starts when the stop is over
          {nextStop && <> and runs to {clockAt(strategy.config.startsAt, nextStop.atMinute)}.</>}
        </div>
      ) : nextStop ? (
        <div
          className={`lsp-next lsp-next--${urgencyOf(nextStop.inMinutes, dueSoonMinutes)}`}
          role="status"
        >
          <div className="lsp-count">
            {nextStop.inMinutes <= 0 ? (
              <>
                <b>{nextStop.inMinutes < -2 ? 'Overdue' : 'Box now'}</b>
                {nextStop.inMinutes < -2 && <span> {formatCountdown(nextStop.inMinutes)}</span>}
              </>
            ) : (
              <>
                Box in <b>{formatCountdown(nextStop.inMinutes)}</b>
              </>
            )}
          </div>
          <div className="lsp-what">
            Stop {nextStop.stintIndex + 1}:{' '}
            {nextStop.stopKind === 'kart-only' ? 'change kart only' : describeStop(nextStop)}
          </div>
          <div className="lsp-who">
            {nextStop.outDriver ?? '—'} <span aria-hidden="true">→</span>{' '}
            {nextStop.stopKind === 'kart-only' ? 'same driver' : (nextStop.nextDriver ?? '—')}
          </div>
          <div className="lsp-when">
            planned {clockAt(strategy.config.startsAt, nextStop.atMinute)}
            {Math.abs(nextStop.projectedAtMinute - nextStop.atMinute) >= 0.5 && (
              <> · sheet now shows {clockAt(strategy.config.startsAt, nextStop.projectedAtMinute)}</>
            )}
          </div>
          {!nextStop.currentDriverIsPlanned && (
            <div className="lsp-flag">Not the planned driver in the kart.</div>
          )}
        </div>
      ) : (
        <div className="lsp-next lsp-next--calm" role="status">
          Last stint — run to the flag.
        </div>
      )}

      {plan.warnings.length > 0 && (
        <ul className="lsp-notes">
          {plan.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {errors.length > 0 && (
        <div className="lsp-errors" role="alert">
          <b>
            {errors.length} problem{errors.length === 1 ? '' : 's'} with the plan from here:
          </b>{' '}
          {errors[0].message}
        </div>
      )}

      <div className="lsp-totals">
        <span>
          <b>{plan.summary.stops - plan.currentIndex}</b> stops left
        </span>
        <span>
          Driven by plan end{' '}
          {plan.summary.driverTotals
            .filter((d) => d.minutes > 0)
            .map((d) => `${d.name.split(' ')[0]} ${formatMinutes(d.minutes)}`)
            .join(' · ')}
        </span>
      </div>

      <div className="lsp-grid" ref={gridRef}>
        <StintGrid
          stints={plan.plan.stints}
          roster={strategy.roster}
          config={strategy.config}
          violations={plan.violations}
          minPitMinutes={plan.summary.constraints.minPitMinutes}
          directionChange={midpoint ?? null}
          onChange={(index, patch) => live.edit(strategy.id, index, patch)}
          readOnly={live.readOnly}
          live={plan.stints}
        />
      </div>

      {!live.readOnly && (
        <div className="lsp-actions">
          <button
            type="button"
            className="lsp-primary"
            onClick={() => void live.commit(strategy.id)}
            disabled={saving}
            title="Make what has happened so far, and the re-balanced rest, the saved plan"
          >
            {saving ? 'Saving…' : item.dirty ? 'Save plan' : 'Re-plan from here'}
          </button>
          {item.dirty && (
            <button type="button" onClick={() => live.discard(strategy.id)} disabled={saving}>
              Discard edits
            </button>
          )}
          <span className="lsp-hint">
            Stops are recorded as they happen. Saving makes the sheet above the new baseline.
          </span>
        </div>
      )}
    </section>
  );
};

const AttachForm: React.FC<{
  live: UseLiveStrategy;
  teams: Map<string, TeamInfo>;
}> = ({ live, teams }) => {
  const [strategyId, setStrategyId] = useState('');
  const [teamId, setTeamId] = useState('');
  const { loadCandidates } = live;

  useEffect(() => {
    void loadCandidates();
  }, [loadCandidates]);

  const teamList = useMemo(
    () =>
      Array.from(teams.values()).sort(
        (a, b) => (parseInt(a.kartNumber, 10) || 0) - (parseInt(b.kartNumber, 10) || 0),
      ),
    [teams],
  );

  return (
    <div className="lsp-attach">
      <h4>Follow a strategy in this race</h4>
      {live.candidates.length === 0 ? (
        <p className="lsp-muted">
          Nothing to attach yet. Build a plan in the{' '}
          <button type="button" className="lsp-link" onClick={() => navigate('/strategy')}>
            strategy planner
          </button>
          , or ask its owner to share it with you.
        </p>
      ) : (
        <div className="lsp-attach-row">
          <select
            aria-label="Strategy to follow"
            value={strategyId}
            onChange={(e) => setStrategyId(e.target.value)}
          >
            <option value="">Strategy…</option>
            {live.candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} · {c.config.durationHours}h {c.config.category}
                {c.ownerName ? ` · ${c.ownerName}` : ''}
              </option>
            ))}
          </select>
          <select
            aria-label="Team it runs for"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            <option value="">Team…</option>
            {teamList.map((t) => (
              <option key={t.id} value={t.id}>
                {teamLabel(t, t.id)}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!strategyId || !teamId}
            onClick={() => {
              void live.attach(strategyId, teamId).then(() => {
                setStrategyId('');
                setTeamId('');
                return live.loadCandidates();
              });
            }}
          >
            Attach
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * The stint plan, followed live.
 *
 * Shows, for each team that has a strategy attached to this race, how long until
 * the next planned stop and what it changes, and the plan itself with what has
 * actually happened laid over it. Everything on it that is not history can be
 * edited.
 */
export const LiveStrategyPanel: React.FC<LiveStrategyPanelProps> = ({
  live,
  teams,
  eventId,
  dueSoonMinutes,
}) => {
  return (
    <div className="lsp-panel">
      <div className="lsp-header">
        <h3>Stint plan</h3>
        {live.loading && <span className="lsp-muted">loading…</span>}
        {live.readOnly && <span className="lsp-badge">replay — read only</span>}
      </div>

      {live.error && (
        <div className="lsp-error" role="alert">
          {live.error}
          <button type="button" onClick={live.dismissError} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {!eventId ? (
        <p className="lsp-muted">
          Start recording a race to follow a stint plan. Plans are attached to a race and a team.
        </p>
      ) : (
        <>
          {live.plans.map((item) => (
            <PlanCard
              key={item.strategy.id}
              item={item}
              team={teams.get(item.teamId)}
              live={live}
              dueSoonMinutes={dueSoonMinutes}
            />
          ))}

          {live.waiting.map((s) => (
            <p key={s.id} className="lsp-waiting">
              <b>{s.title}</b> is attached to{' '}
              {s.kartNumber ? `kart ${s.kartNumber}` : 'a team'}, which is not on the grid yet
              {live.readOnly ? '.' : ' — or the countdown has not started.'}
            </p>
          ))}

          {live.plans.length === 0 && live.waiting.length === 0 && !live.loading && (
            <p className="lsp-muted">No strategy is attached to this race yet.</p>
          )}

          {!live.readOnly && <AttachForm live={live} teams={teams} />}
        </>
      )}
    </div>
  );
};

export default LiveStrategyPanel;
